import { useState } from "react";
import { Copy, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAgentAccessGrants,
  useCreateAccessGrant,
  useDeleteAccessGrant,
} from "@/hooks/agent/useAgentAccess";
import { addPrivateAccessTokenFragment } from "@/lib/privateAccessToken";
import { Agent } from "@/types/agent/agent";
import ShareSettingsSection from "./ShareSettingsSection";

interface AccessGrantsSectionProps {
  genie: Agent;
  shareUrl: string;
}

const EXPIRATION_OPTIONS = [
  { label: "24 hours", hours: 24 },
  { label: "7 days", hours: 168 },
  { label: "30 days", hours: 720 },
  { label: "Never", hours: null },
];

const DEFAULT_BRAND_COLOR = "#4E9CFF";

export default function AccessGrantsSection({
  genie,
  shareUrl,
}: AccessGrantsSectionProps) {
  const { data: grants = [], isLoading } = useAgentAccessGrants(genie.id);
  const createMutation = useCreateAccessGrant(genie.id);
  const deleteMutation = useDeleteAccessGrant(genie.id);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newlyCreatedGrantId, setNewlyCreatedGrantId] = useState<number | null>(
    null,
  );
  const [setExpiration, setSetExpiration] = useState(true);
  const [expirationHours, setExpirationHours] = useState(24);
  const [setMaxUses, setSetMaxUses] = useState(false);
  const [maxUsesValue, setMaxUsesValue] = useState(1);

  const brandColor = genie.branding?.primaryColor || genie.brand_color;

  const buildAccessLink = (token: string) => {
    const url = new URL(shareUrl, window.location.origin);
    return addPrivateAccessTokenFragment(url, token).toString();
  };

  const calculateExpiresAt = (hours: number | null): string | undefined => {
    if (!hours) return undefined;
    const now = new Date();
    now.setHours(now.getHours() + hours);
    return now.toISOString();
  };

  const handleCreate = async () => {
    const expiresAt = setExpiration
      ? calculateExpiresAt(expirationHours)
      : undefined;
    const maxUses = setMaxUses ? maxUsesValue : 0;
    createMutation.mutate(
      { max_uses: maxUses, expires_at: expiresAt },
      {
        onSuccess: (grant) => {
          setNewlyCreatedGrantId(grant.id);
          setShowCreateForm(false);
          setExpirationHours(24);
          setSetExpiration(true);
          setMaxUsesValue(1);
          setSetMaxUses(false);
        },
      },
    );
  };

  const handleCopyLink = (token: string) => {
    navigator.clipboard.writeText(buildAccessLink(token));
    toast.success("Link copied to clipboard!");
  };

  const handleDelete = (grantId: number) => {
    deleteMutation.mutate(grantId);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const summary = isLoading
    ? undefined
    : grants.length === 0
      ? "No links yet"
      : grants.length === 1
        ? "1 link"
        : `${grants.length} links`;

  return (
    <ShareSettingsSection title="Access Links" summary={summary}>
      <div>
        <p className="text-sm text-gray-600 mb-4">
          Create shareable links with token-based access.
        </p>

        {!showCreateForm ? (
          <Button
            onClick={() => setShowCreateForm(true)}
            variant="outline"
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Access Link
          </Button>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4 mb-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="set-expiration"
                  checked={setExpiration}
                  onChange={(e) => setSetExpiration(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label
                  htmlFor="set-expiration"
                  className="text-sm font-medium text-gray-700"
                >
                  Set expiration date
                </label>
              </div>

              {setExpiration && (
                <div className="ml-7">
                  <Select
                    value={expirationHours.toString()}
                    onValueChange={(value) =>
                      setExpirationHours(parseInt(value))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRATION_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.label}
                          value={option.hours?.toString() || "null"}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="set-max-uses"
                  checked={setMaxUses}
                  onChange={(e) => setSetMaxUses(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label
                  htmlFor="set-max-uses"
                  className="text-sm font-medium text-gray-700"
                >
                  Limit uses
                </label>
              </div>

              {setMaxUses && (
                <div className="ml-7">
                  <input
                    type="number"
                    min="1"
                    value={maxUsesValue}
                    onChange={(e) =>
                      setMaxUsesValue(
                        Math.max(1, parseInt(e.target.value) || 1),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                    style={
                      {
                        "--tw-ring-color": brandColor || "DEFAULT_BRAND_COLOR",
                      } as React.CSSProperties
                    }
                    placeholder="Number of uses"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="flex-1"
                style={{ backgroundColor: brandColor || "DEFAULT_BRAND_COLOR" }}
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Link"
                )}
              </Button>
              <Button
                onClick={() => setShowCreateForm(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* New token banner */}
      {newlyCreatedGrantId &&
        (() => {
          const newGrant = grants.find((g) => g.id === newlyCreatedGrantId);
          if (!newGrant?.token) return null;
          return (
            <div
              className="p-4 rounded-lg space-y-3"
              style={{
                backgroundColor: brandColor
                  ? `${brandColor}10`
                  : "rgba(78, 156, 255, 0.1)",
                borderColor: brandColor || DEFAULT_BRAND_COLOR,
                borderWidth: "1px",
              }}
            >
              <p
                className="text-sm font-medium"
                style={{ color: brandColor || DEFAULT_BRAND_COLOR }}
              >
                Your access link is ready — copy it below:
              </p>
              <div
                className="bg-white p-3 rounded font-mono text-xs break-all text-gray-600"
                style={{
                  borderColor: brandColor || DEFAULT_BRAND_COLOR,
                  borderWidth: "1px",
                }}
              >
                {buildAccessLink(newGrant.token)}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleCopyLink(newGrant.token!)}
                  variant="ghost"
                  style={{ color: brandColor || DEFAULT_BRAND_COLOR }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <button
                  onClick={() => setNewlyCreatedGrantId(null)}
                  className="text-sm px-3"
                  style={{ color: brandColor || DEFAULT_BRAND_COLOR }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })()}

      {/* Grants list */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : grants.length > 0 ? (
        <div className="space-y-2">
          <h5 className="text-sm font-medium text-gray-700">Active links</h5>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {grants.map((grant) => (
              <div
                key={grant.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 mb-1">
                    {grant.label && (
                      <span
                        className="font-mono"
                        style={{ color: brandColor || "DEFAULT_BRAND_COLOR" }}
                      >
                        {grant.label}
                      </span>
                    )}
                    {grant.label && " • "}
                    Created {formatDate(grant.created_at)}
                  </p>
                  <p className="text-xs text-gray-500 space-y-0.5">
                    <div>
                      {grant.max_uses === 0
                        ? "Unlimited uses"
                        : `${grant.use_count}/${grant.max_uses} uses`}
                      {grant.expires_at &&
                        ` • Expires ${formatDate(grant.expires_at)}`}
                    </div>
                    {grant.last_used_at && (
                      <div>Last used {formatDate(grant.last_used_at)}</div>
                    )}
                  </p>
                </div>
                <div className="flex items-center ml-2 flex-shrink-0">
                  {grant.token && (
                    <button
                      onClick={() => handleCopyLink(grant.token!)}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      style={{ color: brandColor || DEFAULT_BRAND_COLOR }}
                      aria-label="Copy link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(grant.id)}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    aria-label="Delete link"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">No access links created yet.</p>
        </div>
      )}
    </ShareSettingsSection>
  );
}
